<template>
  <div
    v-if="isVisible"
    class="delivery-info-form"
    :class="`delivery-info-form--${variant}`"
  >
    <PharmacyDeliveryInfoSection
      v-if="isPharmacy"
      ref="pharmacySectionRef"
      v-model="pharmacyInfo"
      :disabled="disabled"
      @update:result="onChildResult"
    />
    <SpecialRequestDeliveryInfoSection
      v-else
      ref="specialRequestSectionRef"
      v-model="specialRequestInfo"
      :disabled="disabled"
      @update:result="onChildResult"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';

import { useCustomer } from '@/use/customer';
import {
  RideServiceType,
  type DeliveryInfoFormResult,
  type PharmacyDeliveryInfo,
  type SpecialRequestDeliveryInfo,
} from '@/models/ride';
import {
  createEmptyPharmacyDeliveryInfo,
  createEmptySpecialRequestDeliveryInfo,
  emptyDeliveryInfoFormResult,
  emptyDeliveryValidationResult,
  isDeliveryServiceType,
  toBirthdayDate,
  type DeliveryValidationResult,
} from '@/Utils/deliveryInfoForm';

import PharmacyDeliveryInfoSection from './PharmacyDeliveryInfo.vue';
import SpecialRequestDeliveryInfoSection from './SpecialRequestDeliveryInfo.vue';
import { canOfferNashDelivery } from '@/services/operators/viewModel';

export type DeliveryInfoFormVariant = 'legacy' | 'v2';

type DeliverySectionExpose = {
  validate: () => DeliveryValidationResult;
};

const props = withDefaults(
  defineProps<{
    serviceType: string;
    disabled?: boolean;
    /** Visual shell: V2 stacked fields by default; legacy uses Bootstrap-like label/control rows. */
    variant?: DeliveryInfoFormVariant;
  }>(),
  {
    disabled: false,
    variant: 'v2',
  },
);

const emit = defineEmits<{
  (event: 'update:result', value: DeliveryInfoFormResult): void;
}>();

const { customerFullName, customerInfo } = useCustomer();

const pharmacySectionRef = ref<DeliverySectionExpose | null>(null);
const specialRequestSectionRef = ref<DeliverySectionExpose | null>(null);

const pharmacyInfo = ref<PharmacyDeliveryInfo>(
  createEmptyPharmacyDeliveryInfo({
    patientName: customerFullName.value,
    birthday: customerInfo.value.dob,
  }),
);
const specialRequestInfo = ref<SpecialRequestDeliveryInfo>(
  createEmptySpecialRequestDeliveryInfo({
    recipientName: customerFullName.value,
  }),
);

const isPharmacy = computed(() => props.serviceType === RideServiceType.PharmacyDelivery);
const isVisible = computed(() => isDeliveryServiceType(props.serviceType) && canOfferNashDelivery.value);

const activeSectionRef = computed(() =>
  isPharmacy.value ? pharmacySectionRef.value : specialRequestSectionRef.value,
);

const onChildResult = (result: DeliveryInfoFormResult): void => {
  emit('update:result', result);
};

const reset = (): void => {
  const fullName = customerFullName.value ?? null;

  pharmacyInfo.value = createEmptyPharmacyDeliveryInfo({
    patientName: fullName,
    birthday: customerInfo.value.dob,
  });
  specialRequestInfo.value = createEmptySpecialRequestDeliveryInfo({
    recipientName: fullName,
  });

  if (!isVisible.value) {
    emit('update:result', emptyDeliveryInfoFormResult());
  }
};

const validate = (): DeliveryValidationResult => {
  if (!isVisible.value) {
    return emptyDeliveryValidationResult();
  }

  return (
    activeSectionRef.value?.validate() ?? {
      isValid: false,
      errors: ['Please complete the delivery information before getting an estimate.'],
    }
  );
};

watch(
  isVisible,
  (visible) => {
    if (!visible) {
      emit('update:result', emptyDeliveryInfoFormResult());
    }
  },
  { immediate: true },
);

watch(() => customerInfo.value.dob,
  (dob) => {
    if (!pharmacyInfo.value.birthday && dob) {
      pharmacyInfo.value.birthday = toBirthdayDate(dob);
    }
  },
);

watch(() => customerFullName.value,
  (fullName) => {
    if (!pharmacyInfo.value.patientName && fullName) {
      pharmacyInfo.value.patientName = fullName;
    }
    if (!specialRequestInfo.value.recipientName && fullName) {
      specialRequestInfo.value.recipientName = fullName;
    }
  },
);

defineExpose({
  reset,
  validate,
});
</script>

<style lang="scss" scoped>
.delivery-info-form {
  --delivery-border: #e5e5e5;
  --delivery-bg: #fff;
  --delivery-title-color: #333;
  --delivery-label-color: #333;
  --delivery-help-color: #666;
  --delivery-error-color: #d93025;
  --delivery-gap: 14px;
  --delivery-field-gap: 6px;

  padding: 16px;
  margin: 12px 0;
  border: 1px solid var(--delivery-border);
  border-radius: 8px;
  background: var(--delivery-bg);

  :deep(.delivery-info-section) {
    display: flex;
    flex-direction: column;
    gap: var(--delivery-gap);
  }

  :deep(.delivery-info-section__title) {
    margin: 0 0 4px;
    font-size: 15px;
    font-weight: 700;
    color: var(--delivery-title-color);
  }

  :deep(.delivery-info-section__field) {
    display: flex;
    flex-direction: column;
    gap: var(--delivery-field-gap);
  }

  :deep(.delivery-info-section__label) {
    font-size: 12px;
    font-weight: 600;
    color: var(--delivery-label-color);
    text-transform: uppercase;
  }

  :deep(.delivery-info-section__control) {
    width: 100%;
  }

  :deep(.delivery-info-section__help) {
    font-size: 12px;
    color: var(--delivery-help-color);
  }

  :deep(.delivery-info-section__error) {
    font-size: 12px;
    color: var(--delivery-error-color);
  }
}

/* OrderRides (Bootstrap): label | control rows, no card chrome */
.delivery-info-form--legacy {
  --delivery-title-color: #73040c;
  --delivery-label-color: inherit;
  --delivery-gap: 10px;
  --delivery-field-gap: 4px;

  padding: 0;
  margin: 10px 0;
  border: none;
  border-radius: 0;
  background: transparent;

  :deep(.delivery-info-section__title) {
    width: 100%;
    margin: 10px 0;
    font-size: 16px;
    font-weight: 400;
    line-height: 27px;
    text-align: left;
    text-transform: none;
    border-bottom: 1px solid #f0f0f0;
  }

  :deep(.delivery-info-section__field) {
    display: grid;
    grid-template-columns: minmax(120px, 25%) minmax(0, 1fr);
    column-gap: 15px;
    row-gap: 4px;
    align-items: start;
  }

  :deep(.delivery-info-section__label) {
    grid-column: 1;
    padding-top: 7px;
    font-size: inherit;
    font-weight: 400;
    text-transform: none;
    text-align: right;
  }

  :deep(.delivery-info-section__field > :not(.delivery-info-section__label)) {
    grid-column: 2;
    max-width: 100%;
  }

  :deep(.delivery-info-section__control),
  :deep(.el-select),
  :deep(.el-date-editor),
  :deep(.el-input),
  :deep(.el-textarea) {
    width: 100%;
    max-width: 420px;
  }
}
</style>
